import { Globe, Lock, ShieldAlert } from "lucide-react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import {
  STATUS_LABEL,
  type CountryCode,
  type TxStatus,
} from "@/types/dashboard";

import "flag-icons/css/flag-icons.min.css";
import { countryFlagCode } from "@/lib/countries";

export function Flag({
  code,
  className,
}: {
  code: CountryCode;
  className?: string;
}) {
  if (code === "INT")
    return (
      <span
        aria-hidden
        className={cn(
          "inline-flex h-4 w-6 items-center justify-center rounded-[3px] bg-primary/12 text-primary ring-1 ring-primary/20",
          className,
        )}
      >
        <Globe className="h-3 w-3" />
      </span>
    );
  return (
    <span
      aria-hidden
      className={cn(
        `fi fi-${countryFlagCode(code)} shrink-0 rounded-[3px] ring-1 ring-black/10 shadow-sm`,
        className,
      )}
    />
  );
}

const BASE = import.meta.env.BASE_URL;
export function Logo({ light }: { light?: boolean }) {
  if (light)
    return (
      <span className="flex items-center">
        <img
          src={`${BASE}brand/bizconnectpay-logo-light.png`}
          alt="BizConnectPay"
          width={1000}
          height={219}
          className="hidden h-auto w-[200px] max-w-none sm:block"
        />
        <img
          src={`${BASE}brand/bizconnectpay-symbol.png`}
          alt="BizConnectPay"
          width={232}
          height={256}
          className="h-8 w-auto sm:hidden"
        />
      </span>
    );
  return (
    <span className="flex items-center gap-2">
      <img
        src={`${BASE}brand/bizconnectpay-symbol.png`}
        alt=""
        aria-hidden
        width={232}
        height={256}
        className="h-8 w-auto"
      />
      <span className="text-[17px] font-bold tracking-tight text-[#0B1F4B]">
        Biz<span className="text-[#00B88A]">Connect</span>
        <span className="text-[#0066FF]">Pay</span>
      </span>
    </span>
  );
}

const TONE: Record<TxStatus, string> = {
  COMPLETED: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  PENDING: "bg-amber-50 text-amber-700 ring-amber-600/20",
  PROCESSING: "bg-blue-50 text-blue-700 ring-blue-600/15",
  FAILED: "bg-rose-50 text-rose-700 ring-rose-600/15",
};
export function StatusPill({ status }: { status: TxStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
        TONE[status],
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {STATUS_LABEL[status]}
    </span>
  );
}

export function EnvironmentBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-emerald-800",
        className,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Données API
    </span>
  );
}

export function Panel({
  title,
  sub,
  action,
  children,
  className,
}: {
  title?: string;
  sub?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rise rounded-xl border bg-card p-5 shadow-[0_1px_0_hsl(222_75%_17%/0.04),0_8px_24px_-16px_hsl(222_75%_17%/0.16)]",
        className,
      )}
    >
      {(title || action) && (
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            {title && (
              <h2 className="text-[15px] font-bold tracking-tight">{title}</h2>
            )}
            {sub && (
              <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
            )}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function KybLock({ what }: { what: string }) {
  return (
    <div
      data-testid="panel-kyb-lock"
      className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-primary/30 bg-accent/50 p-5"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Lock className="h-4 w-4" />
      </span>
      <div>
        <p className="text-sm font-semibold">
          {what} indisponible — KYB requis
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Ce compte entreprise est en statut <b>PENDING</b>. Les fonctions
          financières, l'équipe, les développeurs et les liens de paiement
          s'activent après approbation du KYB.
        </p>
      </div>
      <Link
        href="/verification"
        data-testid="link-complete-kyb"
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:brightness-110"
      >
        <ShieldAlert className="h-3.5 w-3.5" />
        Compléter le KYB
      </Link>
    </div>
  );
}

export function PageHead({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="rise">
        <h1 className="text-[28px] font-extrabold tracking-tight text-primary">
          {title}
        </h1>
        {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
      </div>
      {children && (
        <div className="flex flex-wrap items-center gap-2">{children}</div>
      )}
    </div>
  );
}
